import React from 'react';
import { AutoPathwayConfig } from '../../types/simulation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/Card';
import { Slider } from '../ui/Slider';
import {
  Sliders,
  Zap,
  Crown,
  Users,
} from 'lucide-react';

export interface PathwayControlsProps {
  config: AutoPathwayConfig;
  onChange: (configUpdate: Partial<AutoPathwayConfig>) => void;
  className?: string;
}

const PRESET_SCENARIOS: {
  title: string;
  badge: string;
  config: Partial<AutoPathwayConfig>;
}[] = [
  {
    title: 'Помірний темп',
    badge: '1-2 год/день',
    config: {
      newbieMeetingsPerMonth: 10,
      leaderMeetingsPerMonth: 5,
      leaderPersonalMeetings: 10,
      conversionRate: 0.10,
      meetingsPerContract: 10,
      contractSizeAE: 50.0,
      averageContractAE: 50.0,
      recruitmentPace: 1.0,
      teamSeminarRecruitPercent: 15,
      leaderRecruitmentPace: 1.0,
      attritionRate: 0.30,
      preOnboardingAttritionRate: 0.30,
      leaderCeaseLevel: 4,
    },
  },
  {
    title: 'Стандартний бізнес (Part-time)',
    badge: '3-4 год/день',
    config: {
      newbieMeetingsPerMonth: 20,
      leaderMeetingsPerMonth: 10,
      leaderPersonalMeetings: 20,
      conversionRate: 0.10,
      meetingsPerContract: 10,
      contractSizeAE: 50.0,
      averageContractAE: 50.0,
      recruitmentPace: 1.0,
      teamSeminarRecruitPercent: 20,
      leaderRecruitmentPace: 1.0,
      attritionRate: 0.30,
      preOnboardingAttritionRate: 0.30,
      leaderCeaseLevel: 4,
    },
  },
  {
    title: 'Агресивне масштабування (Full-time)',
    badge: '6-8 год/день',
    config: {
      newbieMeetingsPerMonth: 30,
      leaderMeetingsPerMonth: 10,
      leaderPersonalMeetings: 30,
      conversionRate: 0.10,
      meetingsPerContract: 10,
      contractSizeAE: 50.0,
      averageContractAE: 50.0,
      recruitmentPace: 1.0,
      teamSeminarRecruitPercent: 25,
      leaderRecruitmentPace: 1.5,
      attritionRate: 0.30,
      preOnboardingAttritionRate: 0.30,
      leaderCeaseLevel: 5,
    },
  },
];

export const PathwayControls: React.FC<PathwayControlsProps> = ({
  config,
  onChange,
  className = '',
}) => {
  const leaderMeetingsValue = config.leaderPersonalMeetings ?? config.newbieMeetingsPerMonth;
  const leaderRecruitValue = config.leaderRecruitmentPace ?? config.recruitmentPace;
  const leaderCeaseLevel = config.leaderCeaseLevel ?? 0;
  const teamCeaseLevel = config.teamCeaseLevel ?? 0;

  const currentAvgAE = config.averageContractAE ?? config.contractSizeAE ?? 50;
  const [contractAEInput, setContractAEInput] = React.useState<string>(String(currentAvgAE));

  React.useEffect(() => {
    setContractAEInput(String(currentAvgAE));
  }, [currentAvgAE]);

  const handleContractAEChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setContractAEInput(raw);
    const val = parseFloat(raw);
    if (!isNaN(val) && val >= 30 && val <= 100) {
      onChange({ averageContractAE: val, contractSizeAE: val });
    }
  };

  const handleContractAEBlur = () => {
    let val = parseFloat(contractAEInput);
    if (isNaN(val) || val < 30) {
      val = 30;
    } else if (val > 100) {
      val = 100;
    }
    setContractAEInput(String(val));
    onChange({ averageContractAE: val, contractSizeAE: val });
  };

  return (
    <Card variant="elevated" className={`border-slate-700/80 overflow-hidden ${className}`}>
      <CardHeader className="bg-gradient-to-r from-slate-900 via-slate-800/80 to-slate-900 border-b border-white/10 p-3.5 sm:p-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent-emerald/15 border border-accent-emerald/30 flex items-center justify-center text-accent-emerald flex-shrink-0">
            <Sliders className="w-4 h-4" />
          </div>
          <div>
            <CardTitle className="text-sm sm:text-base">Параметри симуляції</CardTitle>
            <CardDescription className="text-[11px]">
              Роздільні налаштування для лідера та консультантів команди
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-3.5 sm:p-4 space-y-5">
        {/* Scenario Presets */}
        <div className="space-y-2">
          <label className="text-[11px] font-semibold text-text-primary uppercase tracking-wider flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-accent-neon" />
            Готові стратегічні сценарії:
          </label>
          <div className="flex flex-col gap-1.5">
            {PRESET_SCENARIOS.map((sc, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onChange(sc.config)}
                className="
                  p-2.5 rounded-xl bg-surface-hover/70 hover:bg-surface-hover border border-white/5
                  hover:border-accent-emerald/40 transition-all text-left flex items-center justify-between
                  cursor-pointer group
                "
              >
                <span className="font-semibold text-xs text-text-primary group-hover:text-accent-emerald transition-colors">
                  {sc.title}
                </span>
                <span className="text-[10px] text-accent-neon bg-emerald-500/10 px-2 py-0.5 rounded-md font-mono border border-emerald-500/20">
                  {sc.badge}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Simulation Duration Selector */}
        <div className="space-y-2 pt-2 border-t border-white/5">
          <label className="text-[11px] font-semibold text-text-primary uppercase tracking-wider">
            Тривалість прогнозу:
          </label>
          <div className="grid grid-cols-5 gap-1 sm:gap-1.5 w-full">
            {[
              { m: 6, label: '6 міс' },
              { m: 12, label: '1 рік' },
              { m: 24, label: '2 роки' },
              { m: 36, label: '3 роки' },
              { m: 60, label: '5 років' },
            ].map(({ m, label }) => (
              <button
                key={m}
                type="button"
                title={`Прогноз на ${label}`}
                onClick={() => onChange({ months: m })}
                className={`
                  min-h-[32px] h-[32px] flex items-center justify-center py-1 px-0.5 sm:px-1.5 rounded-lg text-[10.5px] sm:text-xs font-mono font-semibold tracking-tight transition-all cursor-pointer border text-center whitespace-nowrap leading-none box-border min-w-0 truncate
                  ${
                    config.months === m
                      ? 'bg-accent-emerald text-slate-950 border-accent-emerald shadow-[0_0_10px_rgba(16,185,129,0.35)]'
                      : 'bg-surface-hover text-text-secondary hover:text-text-primary border-white/5 hover:border-white/20'
                  }
                `}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* SECTION A: Leader-Specific Settings */}
        <div className="space-y-3.5 pt-3 border-t border-emerald-500/20 bg-emerald-950/10 -mx-3.5 sm:-mx-4 px-3.5 sm:px-4 py-3 rounded-xl border">
          <div className="flex items-center gap-2 text-xs font-bold text-accent-emerald uppercase tracking-wider">
            <Crown className="w-4 h-4 text-accent-neon flex-shrink-0" />
            <span>Налаштування лідера (Ви)</span>
          </div>

          <div className="space-y-3.5">
            {/* 1. Leader personal meetings */}
            <Slider
              label="Особисті зустрічі лідера"
              description="Кількість презентацій на місяць, що проводить особисто лідер"
              value={leaderMeetingsValue}
              min={0}
              max={40}
              step={1}
              unit="зустрічей/міс"
              onChange={(val) => onChange({ leaderPersonalMeetings: val })}
            />

            {/* 2. Leader recruitment pace */}
            <Slider
              label="Особистий рекрутинг лідера"
              description="Кількість нових партнерів, залучених особисто лідером на місяць"
              value={leaderRecruitValue}
              min={0}
              max={4.0}
              step={0.1}
              valueFormatter={(val) => `${val.toFixed(1)} чол./міс`}
              onChange={(val) => onChange({ leaderRecruitmentPace: val })}
            />

            {/* 3. Leader cease personal activity cutoff level */}
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-1 text-xs">
                <span className="font-semibold text-text-primary">
                  Припинення особистих продажів:
                </span>
                <span className="font-mono text-[11px] text-accent-neon font-semibold shrink-0">
                  {leaderCeaseLevel === 0 ? 'Без обмежень' : `З Рівня ${leaderCeaseLevel}+`}
                </span>
              </div>
              <p className="text-[11px] text-text-muted leading-tight">
                На вищих рівнях лідер фокусується на управлінні структурою (особисті продажі = 0)
              </p>
              <div className="grid grid-cols-4 gap-1.5 pt-1">
                {[
                  { lvl: 0, label: 'Завжди', title: 'Завжди активні особисті продажі лідера (без припинення)' },
                  { lvl: 4, label: 'Р4+', title: 'Припинити особисті продажі лідера з Рівня 4+' },
                  { lvl: 5, label: 'Р5+', title: 'Припинити особисті продажі лідера з Рівня 5+' },
                  { lvl: 6, label: 'Р6+', title: 'Припинити особисті продажі лідера з Рівня 6+' },
                ].map(({ lvl, label, title }) => (
                  <button
                    key={lvl}
                    type="button"
                    title={title}
                    onClick={() => onChange({ leaderCeaseLevel: lvl })}
                    className={`
                      min-h-[30px] h-[30px] flex items-center justify-center py-1 px-1 sm:px-2 rounded-lg text-[11px] sm:text-xs font-mono font-medium tracking-tight transition-all cursor-pointer border text-center whitespace-nowrap box-border min-w-0
                      ${
                        leaderCeaseLevel === lvl
                          ? 'bg-amber-500 text-slate-950 font-bold border-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.4)]'
                          : 'bg-surface-hover/80 text-text-secondary hover:text-white border-white/5'
                      }
                    `}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION B: Team & Structure Settings */}
        <div className="space-y-3.5 pt-2 border-t border-white/5">
          <div className="flex items-center gap-2 text-xs font-bold text-cyan-400 uppercase tracking-wider">
            <Users className="w-4 h-4 text-cyan-400 flex-shrink-0" />
            <span>Налаштування команди (Консультанти)</span>
          </div>

          <div className="space-y-3.5">
            {/* 1. Newbie meetings */}
            <Slider
              label="Зустрічі новеньких (1–3 міс)"
              description="Кількість презентацій на місяць у стартовий період партнера"
              value={config.newbieMeetingsPerMonth}
              min={4}
              max={40}
              step={1}
              unit="зустрічей/міс"
              onChange={(val) => onChange({ newbieMeetingsPerMonth: val })}
            />

            {/* 2. Team Consultant meetings */}
            <Slider
              label="Зустрічі досвідчених (4+ міс)"
              description="Особисті зустрічі партнерів команди після закріплення в бізнесі"
              value={config.leaderMeetingsPerMonth}
              min={2}
              max={30}
              step={1}
              unit="зустрічей/міс"
              onChange={(val) => onChange({ leaderMeetingsPerMonth: val })}
            />

            {/* 3. Conversion Metric: Meetings per contract (3 to 20) */}
            <Slider
              label="Бесід на 1 контракт"
              description="Кількість бесід/зустрічей для укладання 1 контракту в середньому"
              value={
                config.meetingsPerContract ??
                (config.conversionRate > 0 ? Math.min(20, Math.max(3, Math.round(1 / config.conversionRate))) : 10)
              }
              min={3}
              max={20}
              step={1}
              valueFormatter={(val) => `${val} бесід (${(100 / val).toFixed(1)}%)`}
              onChange={(val) => onChange({ meetingsPerContract: val, conversionRate: 1 / val })}
            />

            {/* 4. Average Contract Size (AE) - Direct numeric input box (not slider, 30 to 100 AE) */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <label htmlFor="avg-contract-ae" className="font-semibold text-text-primary">
                  Розмір середнього контракту (Е):
                </label>
                <span className="font-mono text-[11px] text-cyan-400 font-semibold">
                  {config.averageContractAE ?? config.contractSizeAE ?? 50} Е
                </span>
              </div>
              <p className="text-[11px] text-text-muted leading-tight">
                Середній обсяг полісу в одиницях (діапазон 30 – 100 Е, за замовчуванням 50 Е)
              </p>
              <div className="relative">
                <input
                  id="avg-contract-ae"
                  type="number"
                  min={30}
                  max={100}
                  step={1}
                  value={contractAEInput}
                  onChange={handleContractAEChange}
                  onBlur={handleContractAEBlur}
                  className="w-full px-3 py-2 text-base sm:text-xs rounded-xl bg-slate-900/90 text-text-primary border border-white/10 focus:border-cyan-400 focus:outline-none font-mono"
                  placeholder="50"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-text-muted font-mono pointer-events-none">
                  Е
                </span>
              </div>
            </div>

            {/* 5. Pre-onboarding Attrition Rate (20% to 50%, default 30%, shared for leader and team) */}
            <Slider
              label="Відсів до запуску в бізнес"
              description="Частка відсіяних партнерів після оформлення полісу до старту активної роботи (спільно для лідера і команди)"
              value={Math.round((config.preOnboardingAttritionRate ?? 0.30) * 100)}
              min={20}
              max={50}
              step={5}
              valueFormatter={(val) => `${val}%`}
              onChange={(val) => onChange({ preOnboardingAttritionRate: val / 100 })}
            />

            {/* 6. Downline Recruitment Pace (before Leader Level 4) */}
            <Slider
              label="Темп рекрутингу команди (до Р4 лідера)"
              description="Кількість нових партнерів на місяць до виходу лідера на 4-й рівень"
              value={config.recruitmentPace}
              min={0.2}
              max={4.0}
              step={0.1}
              valueFormatter={(val) => `${val.toFixed(1)} чол./міс`}
              onChange={(val) => onChange({ recruitmentPace: val })}
            />

            {/* 7. Team Seminar Recruitment Rate (% of structure, Leader Level 4+) */}
            <Slider
              label="Рекрутинг на семінарах команди (з Р4+ лідера)"
              description="Відсоток нових учасників семінару від загальної кількості людей у структурі"
              value={config.teamSeminarRecruitPercent ?? 20}
              min={5}
              max={40}
              step={1}
              valueFormatter={(val) => `${val}% від структури`}
              onChange={(val) => onChange({ teamSeminarRecruitPercent: val })}
            />

            {/* Explanatory callout for team recruitment transition */}
            <div className="p-2.5 rounded-lg bg-cyan-950/30 border border-cyan-500/20 text-[11px] text-cyan-300/90 flex items-start gap-2">
              <span className="font-bold shrink-0">💡</span>
              <span className="leading-tight">
                <strong>Масштабування рекрутингу:</strong> До 4-го рівня лідера команда залучає за фіксованим темпом ({config.recruitmentPace.toFixed(1)} чол./міс). З досягненням 4-го рівня рекрутинг команди автоматично переходить у відсоток ({config.teamSeminarRecruitPercent ?? 20}%) нових учасників на семінарі від загальної чисельності структури.
              </span>
            </div>

            {/* 7. Attrition / Dormant Rate */}
            <Slider
              label="Коефіцієнт відсіву (Dormant)"
              description="Частка партнерів, які стають неактивними (зберігають місце в дереві)"
              value={Math.round(config.attritionRate * 100)}
              min={0}
              max={50}
              step={5}
              valueFormatter={(val) => `${val}%`}
              onChange={(val) => onChange({ attritionRate: val / 100 })}
            />

            {/* 6. Team Cease Personal Sales Cutoff Level */}
            <div className="space-y-1.5 pt-1">
              <div className="flex flex-wrap items-center justify-between gap-1 text-xs">
                <span className="font-semibold text-text-primary">
                  Припинення особистих продажів у команді:
                </span>
                <span className="font-mono text-[11px] text-cyan-400 font-semibold shrink-0">
                  {teamCeaseLevel === 0 ? 'Без обмежень' : `З Рівня ${teamCeaseLevel}+`}
                </span>
              </div>
              <p className="text-[11px] text-text-muted leading-tight">
                Консультанти в команді припиняють особисті зустрічі з цього рівня і переходять на управління
              </p>
              <div className="grid grid-cols-4 gap-1.5 pt-1">
                {[
                  { lvl: 0, label: 'Завжди', title: 'Завжди активні особисті продажі команди (без припинення)' },
                  { lvl: 4, label: 'Р4+', title: 'Припинити особисті продажі команди з Рівня 4+' },
                  { lvl: 5, label: 'Р5+', title: 'Припинити особисті продажі команди з Рівня 5+' },
                  { lvl: 6, label: 'Р6+', title: 'Припинити особисті продажі команди з Рівня 6+' },
                ].map(({ lvl, label, title }) => (
                  <button
                    key={lvl}
                    type="button"
                    title={title}
                    onClick={() => onChange({ teamCeaseLevel: lvl })}
                    className={`
                      min-h-[30px] h-[30px] flex items-center justify-center py-1 px-1 sm:px-2 rounded-lg text-[11px] sm:text-xs font-mono font-medium tracking-tight transition-all cursor-pointer border text-center whitespace-nowrap box-border min-w-0
                      ${
                        teamCeaseLevel === lvl
                          ? 'bg-cyan-500 text-slate-950 font-bold border-cyan-400 shadow-[0_0_8px_rgba(6,182,212,0.4)]'
                          : 'bg-surface-hover/80 text-text-secondary hover:text-white border-white/5'
                      }
                    `}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
